import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
mapboxgl.accessToken = MAPBOX_TOKEN;

const DEFAULT_CENTER = [151.2093, -33.8688]; // Sydney CBD [lng, lat]

// Convert center prop (object/array) to [lng, lat]
const normaliseCenter = (center) => {
  if (!center) return DEFAULT_CENTER;
  if (Array.isArray(center)) return center;
  if (typeof center === 'object' && center.lat != null && center.lng != null) {
    return [center.lng, center.lat];
  }
  return DEFAULT_CENTER;
};

// Build a circular GeoJSON polygon for the radius outline (turf-free implementation)
const buildCirclePolygon = (centerLngLat, radiusMeters, points = 64) => {
  const [lng, lat] = centerLngLat;
  const coords = [];
  const distanceX = radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));
  const distanceY = radiusMeters / 110540;
  for (let i = 0; i <= points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    coords.push([lng + distanceX * Math.cos(theta), lat + distanceY * Math.sin(theta)]);
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
};

export const MapComponent = ({
  listings = [],
  center,
  zoom = 13,
  onMarkerClick,
  selectedListingId,
  onMapMove,
  interactive = true,
  showUserLocation = true,
  radiusMeters = null,
  radiusCenter = null,
  onUserLocationChange = null,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({});
  const userMarkerRef = useRef(null);
  const popupRef = useRef(null);
  const onMapMoveRef = useRef(onMapMove);
  const onUserLocChangeRef = useRef(onUserLocationChange);
  const onMarkerClickRef = useRef(onMarkerClick);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);

  // Keep latest callbacks in refs so map handlers don't need to rebind
  useEffect(() => { onMapMoveRef.current = onMapMove; }, [onMapMove]);
  useEffect(() => { onUserLocChangeRef.current = onUserLocationChange; }, [onUserLocationChange]);
  useEffect(() => { onMarkerClickRef.current = onMarkerClick; }, [onMarkerClick]);

  // Initialize map (once)
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: normaliseCenter(center),
      zoom,
      interactive,
      attributionControl: false,
    });

    if (interactive) {
      map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    }

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    map.current.on('moveend', () => {
      if (!onMapMoveRef.current || !map.current) return;
      const c = map.current.getCenter();
      const b = map.current.getBounds();
      onMapMoveRef.current({
        center: { lat: c.lat, lng: c.lng },
        bounds: {
          ne: { lat: b.getNorthEast().lat, lng: b.getNorthEast().lng },
          sw: { lat: b.getSouthWest().lat, lng: b.getSouthWest().lng },
        },
      });
    });

    return () => {
      Object.values(markersRef.current).forEach((m) => m.remove());
      markersRef.current = {};
      if (userMarkerRef.current) { userMarkerRef.current.remove(); userMarkerRef.current = null; }
      if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      if (map.current) { map.current.remove(); map.current = null; }
      setMapLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update center when prop changes
  useEffect(() => {
    if (!map.current || !center) return;
    const target = normaliseCenter(center);
    const cur = map.current.getCenter();
    if (Math.abs(cur.lng - target[0]) > 1e-5 || Math.abs(cur.lat - target[1]) > 1e-5) {
      map.current.flyTo({ center: target, zoom, duration: 800 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng, Array.isArray(center) ? center[0] : null, Array.isArray(center) ? center[1] : null]);

  // Render listing markers
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const nextIds = new Set(listings.map((l) => l.listing_id));
    // Remove stale markers
    Object.keys(markersRef.current).forEach((id) => {
      if (!nextIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
      }
    });

    listings.forEach((listing) => {
      const coords = listing.location?.coordinates;
      if (!coords) return;
      const isSelected = selectedListingId === listing.listing_id;
      const existing = markersRef.current[listing.listing_id];

      if (existing) {
        // Update inner pill style only — never touch transform on outer (mapbox owns it)
        const outer = existing.getElement();
        const inner = outer.firstElementChild;
        if (inner) {
          inner.style.background = isSelected ? '#4da855' : '#5ebc67';
          inner.style.width = isSelected ? '52px' : '44px';
          inner.style.height = isSelected ? '32px' : '28px';
        }
        outer.classList.toggle('selected', isSelected);
        outer.style.zIndex = isSelected ? '100' : '1';
        existing.setLngLat([coords[0], coords[1]]);
        return;
      }

      // Outer wrapper — mapbox sets transform: translate3d() on this element to position it.
      // We NEVER write to outer.style.transform. Hover/selected scaling is done via CSS on the inner.
      const outer = document.createElement('div');
      outer.className = `spotfinder-map-marker${isSelected ? ' selected' : ''}`;
      outer.style.cursor = 'pointer';
      outer.style.zIndex = isSelected ? '100' : '1';

      // Inner pill — CSS controls its transform (see App.css .spotfinder-map-marker > div rules)
      const inner = document.createElement('div');
      inner.style.cssText = `
        display:flex;align-items:center;justify-content:center;
        width:${isSelected ? '52px' : '44px'};height:${isSelected ? '32px' : '28px'};
        background:${isSelected ? '#4da855' : '#5ebc67'};
        border-radius:20px;border:2px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,0.18);
        will-change:transform;
      `;
      inner.innerHTML = `<span style="color:#fff;font-size:11px;font-weight:700;font-family:Arial,sans-serif;">$${listing.price_hourly}</span>`;
      outer.appendChild(inner);

      outer.addEventListener('click', (e) => {
        e.stopPropagation();
        if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
        const popupNode = document.createElement('div');
        popupNode.style.cssText = 'min-width:180px;padding:4px;';
        popupNode.innerHTML = `
          <div style="font-weight:600;color:#0f172a;margin-bottom:2px;">${(listing.title || '').replace(/</g, '&lt;')}</div>
          <div style="font-size:12px;color:#64748b;margin-bottom:6px;">${(listing.suburb || '').replace(/</g, '&lt;')}</div>
          <div style="font-size:16px;font-weight:700;color:#5ebc67;">$${listing.price_hourly}/hr</div>
        `;
        popupRef.current = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, offset: 18 })
          .setLngLat([coords[0], coords[1]])
          .setDOMContent(popupNode)
          .addTo(map.current);
        if (onMarkerClickRef.current) onMarkerClickRef.current(listing);
      });
      // Bring to front on hover via z-index only — CSS handles the scale
      outer.addEventListener('mouseenter', () => { outer.style.zIndex = '50'; });
      outer.addEventListener('mouseleave', () => {
        outer.style.zIndex = selectedListingId === listing.listing_id ? '100' : '1';
      });

      markersRef.current[listing.listing_id] = new mapboxgl.Marker({ element: outer })
        .setLngLat([coords[0], coords[1]])
        .addTo(map.current);
    });
  }, [listings, mapLoaded, selectedListingId]);

  // Fly to selected listing
  useEffect(() => {
    if (!map.current || !selectedListingId) return;
    const listing = listings.find((l) => l.listing_id === selectedListingId);
    if (listing?.location?.coordinates) {
      map.current.flyTo({ center: listing.location.coordinates, zoom: 15, duration: 900 });
    }
  }, [selectedListingId, listings]);

  // Get user's current location
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setIsLocating(false);
        if (map.current) {
          map.current.flyTo({ center: [loc.lng, loc.lat], zoom: 14, duration: 800 });
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Auto-locate on mount
  useEffect(() => {
    if (showUserLocation && mapLoaded) {
      getUserLocation();
    }
  }, [showUserLocation, mapLoaded, getUserLocation]);

  // Notify parent when user location changes
  useEffect(() => {
    if (userLocation && onUserLocChangeRef.current) {
      onUserLocChangeRef.current(userLocation);
    }
  }, [userLocation]);

  // Render user location dot
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    if (!userLocation) {
      if (userMarkerRef.current) { userMarkerRef.current.remove(); userMarkerRef.current = null; }
      return;
    }

    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat([userLocation.lng, userLocation.lat]);
      return;
    }

    const dot = document.createElement('div');
    dot.style.cssText = `
      width:18px;height:18px;border-radius:50%;
      background:#4285F4;border:3px solid #fff;
      box-shadow:0 0 0 2px rgba(66,133,244,0.25),0 2px 6px rgba(0,0,0,0.2);
    `;
    userMarkerRef.current = new mapboxgl.Marker({ element: dot })
      .setLngLat([userLocation.lng, userLocation.lat])
      .addTo(map.current);
  }, [userLocation, mapLoaded]);

  // Render radius circle as a source/layer (re-uses on update — no flicker)
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const m = map.current;
    const sourceId = 'spotfinder-radius';
    const fillLayerId = 'spotfinder-radius-fill';
    const lineLayerId = 'spotfinder-radius-line';

    const removeLayers = () => {
      if (m.getLayer(fillLayerId)) m.removeLayer(fillLayerId);
      if (m.getLayer(lineLayerId)) m.removeLayer(lineLayerId);
      if (m.getSource(sourceId)) m.removeSource(sourceId);
    };

    const center = radiusCenter
      ? [radiusCenter.lng ?? radiusCenter[0], radiusCenter.lat ?? radiusCenter[1]]
      : userLocation
        ? [userLocation.lng, userLocation.lat]
        : null;

    if (!radiusMeters || !center) {
      removeLayers();
      return;
    }

    const geojson = buildCirclePolygon(center, radiusMeters);

    if (m.getSource(sourceId)) {
      m.getSource(sourceId).setData(geojson);
    } else {
      m.addSource(sourceId, { type: 'geojson', data: geojson });
      m.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: { 'fill-color': '#5ebc67', 'fill-opacity': 0.08 },
      });
      m.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#5ebc67',
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.8,
        },
      });
    }
  }, [radiusMeters, radiusCenter, userLocation, mapLoaded]);

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-xl overflow-hidden" data-testid="map-container">
      <div ref={mapContainer} className="w-full h-full" />

      {showUserLocation && interactive && (
        <Button
          onClick={getUserLocation}
          disabled={isLocating}
          className="absolute bottom-4 right-4 bg-white hover:bg-slate-50 text-slate-700 shadow-lg rounded-full h-12 w-12 p-0"
          data-testid="my-location-btn"
        >
          {isLocating ? (
            <div className="w-5 h-5 border-2 border-[#5ebc67] border-t-transparent rounded-full animate-spin" />
          ) : (
            <Navigation className="w-5 h-5" />
          )}
        </Button>
      )}
    </div>
  );
};

export default MapComponent;
